import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';
import { calculateAge, dateFormatter } from '../../../../../utils';
import parse from 'html-react-parser';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 'bold' }]
});

const styles = StyleSheet.create({
  page: {
    padding: 20
  },
  heading: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 20
  },
  line: {
    height: 0.6,
    backgroundColor: 'black',
    marginVertical: 10,
    marginTop: '40px'
  },
  line2: {
    height: 0.4,
    backgroundColor: 'black',
    marginVertical: 10
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 0,
    marginBottom: 0,
    border: '1px Solid gray',
    padding: '10px',
    borderRadius: '8px'
  },
  contentContainer2: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 10,
    marginBottom: 0
  },
  column: {
    flex: 1,
    paddingHorizontal: 10
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5
  },
  row2: {
    flexDirection: 'row'
  },
  text: {
    fontSize: 9,
    display: 'inline-block',
    textAlign: 'start'
  },
  textBold: {
    fontSize: 9,
    fontWeight: 'bold'
  },
  textUnderlined: {
    textDecoration: 'underline'
  },
  columnContent: {
    width: '25%',
    textAlign: 'center',
    alignItems: 'center',
    paddingLeft: '10%'
  },
  columnHeader: {
    width: '15%',
    backgroundColor: '',
    borderTop: '0.8px solid black',
    borderBottom: '0.8px solid black',
    color: 'black',
    padding: 5,
    textAlign: 'center',
    marginTop: '2%'
  },
  boldText: {
    fontFamily: 'Roboto',
    fontSize: 6,
    fontSize: '10px',
    marginTop: '1%',
    marginLeft: '4%'
  },
  bText: {
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    fontSize: '10px'
  },
  ResultEntryText: {
    fontFamily: 'Roboto',
    fontSize: '12px',
    margin: '0 auto',
    fontWeight: 'ultrabold'
  },
  fontBold: {
    width: '25%',
    paddingLeft: '10%',
    fontFamily: 'Roboto',
    fontWeight: 'ultrabold',
    textDecoration: 'underline'
  },
  parsedContent: {
    marginTop: 10,
    fontSize: 10,
    textAlign: 'justify',
    padding: 10
  },
  imageStyle: {
    width: '10%',
    height: 80 // You can adjust the height or use percentage if needed
  },
  table: { display: 'table', width: '100%', marginTop: 10 },
  tableRow: { display: 'flex', flexDirection: 'row' },
  tableCell: { display: 'flex', width: '50%', padding: 5, border: '0.6px solid black' }
});

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

const COLORS = ['#f0f0f0', '#e0e0e0'];

export const ResultEntryPdf1 = ({ data }) => {
  const parseHtmlToPdfText = (html) => {
    return parse(html, {
      replace: (domNode) => {
        if (domNode.type === 'tag') {
          switch (domNode.name) {
            case 'strong':
              return <PDFText style={styles.boldText}>{domNode.children[0]?.data}</PDFText>;
            case 'p':
              return <PDFText style={styles.text}>{domNode.children.map((child) => child.data).join('')}</PDFText>;
            default:
              return <PDFText style={styles.text}>{domNode.data}</PDFText>;
          }
        }
      }
    });
  };
  const stripHtml = (html) => {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  };
  const editorContentPlainText = stripHtml(data?.editorContent || '');
  const groupedTests = data?.tests?.reduce((acc, test) => {
    const groupName = test.test_name;
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(test);
    return acc;
  }, {});


  const getRowBackgroundColorForPDF = (test, index) => {
    const resultValue = parseFloat(test?.Result);
    const from = parseFloat(test?.refrence_range_value?.general_from ?? test?.general_from ?? 0);
    const to = parseFloat(test?.refrence_range_value?.general_to ?? test?.general_to ?? 0);

    if (isNaN(resultValue) || (from === 0 && to === 0)) {
      return index % 2 ? 'lightgrey' : 'transparent';
    }

    const lowerMargin = from * 0.1;
    const upperMargin = to * 0.1;

    const lowerOrangeLimit = from - lowerMargin;
    const upperOrangeLimit = to + upperMargin;

    if (resultValue < lowerOrangeLimit || resultValue > upperOrangeLimit) {
      return '#FFCDD2'; // 🔴 Red
    }

    if (
      (resultValue >= lowerOrangeLimit && resultValue < from) ||
      (resultValue > to && resultValue <= upperOrangeLimit)
    ) {
      return '#FFE0B2'; // 🟠 Orange
    }

    return index % 2 ? 'lightgrey' : 'transparent'; // zebra striping
  };
  return (
    <PDFViewer width="100%" height="100%" showToolbar>
      <Document title="Result Entry">
        {groupedTests &&
          Object.keys(groupedTests).map((testName, idx) => (
            <Page key={idx} size="A4" style={styles.page}>
              <View style={{ ...styles.pageContent, marginTop: '25%' }} wrap={true} minPresenceAhead={80}>
                {/* Patient Details Section */}
                <View style={styles.contentContainer}>
                  <View style={{ ...styles.column, marginLeft: '1%' }}>
                    <View style={styles.row}>
                      <Text content="Name               :     " style={styles.bText} />
                      <Text content={`${data?.patientName}`} />
                    </View>
                    <View style={styles.row}>
                      <Text content="Age/Gender    :      " style={styles.bText} />
                      <Text content={`${calculateAge(data?.age)}/${data?.gender}`} />
                    </View>
                    <View style={styles.row}>
                      <Text content="Phone No.       :      " style={styles.bText} />
                      <Text content={`${data?.mobile_no}`} />
                    </View>
                    <View style={styles.row}>
                      <Text content="Report Date    :      " style={styles.bText} />
                      <Text content={`${dateFormatter(data?.createdAt)}`} />
                    </View>
                  </View>

                  <View style={styles.column}>
                    <View style={styles.row}>
                      <Text content="UHID                       :     " style={styles.bText} />
                      <Text content={`${data?.uhid}`} />
                    </View>
                    <View style={styles.row}>
                      <Text content="Report Id                :     " style={styles.bText} />
                      <Text content={`${data?.report_id}`} />
                    </View>
                    <View style={styles.row}>
                      <Text content="Collection Date     :     " style={styles.bText} />
                      <Text content={`${dateFormatter(data?.sampleCollection?.date)}`} />
                    </View>
                    <View style={styles.row}>
                      <Text content="Doctor Advised     :     " style={styles.bText} />
                      <Text content={data?.doctor?.name} />
                    </View>
                  </View>
                </View>

                {/* Table Header */}
                <View style={{ ...styles.row2, width: '100%', flexDirection: 'row', marginTop: '10px' }}>
                  <Text style={{
                    ...styles.columnHeader, width: '50%',
                    fontSize: '12px',
                    fontFamily: 'Roboto',
                    textAlign: 'start'
                  }} content="Test Name" />
                  <Text style={{ ...styles.columnHeader, width: '10%', fontSize: '12px', fontFamily: 'Roboto' }} content="Result" />
                  <Text style={{ ...styles.columnHeader, width: '10%', fontSize: '12px', fontFamily: 'Roboto' }} content="Low/High" />
                  <Text style={{ ...styles.columnHeader, width: '15%', fontSize: '12px', fontFamily: 'Roboto' }} content="Biological Ref. Interval" />
                  <Text style={{ ...styles.columnHeader, width: '10%', fontSize: '12px', fontFamily: 'Roboto' }} content="Unit" />
                </View>

                {/* Test Group Title */}
                <Text
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontFamily: 'Roboto',
                    textDecoration: 'underline',
                    marginTop: '10px',
                    marginBottom: '5px'
                  }}
                  content={testName}
                />

                {/* Test Items */}
                {groupedTests[testName].map((test, testIndex) => (
                  <View key={testIndex} style={{ flexDirection: 'row', marginBottom: '13px', backgroundColor: getRowBackgroundColorForPDF(test, testIndex) }}>
                    <Text
                      content={test.test_item_name || '-'}
                      style={{
                        ...styles.columnText,
                        width: '48%',
                        textAlign: 'start',
                        fontSize: '8px',
                        marginLeft: '10px',

                      }}
                    />
                    <Text
                      content={test.Result || '-'}
                      style={{
                        ...styles.columnText,
                        width: '10%',
                        textAlign: 'center',
                        fontFamily:
                          test.Result &&
                            test.general_from &&
                            test.general_to &&
                            test.Result >= test.general_from &&
                            test.Result <= test.general_to
                            ? ''
                            : 'Roboto',
                        fontSize: '8px'
                      }}
                    />
                    <Text
                      content={
                        test.Result && test.general_from && test.general_to
                          ? test.Result > test.general_to
                            ? 'H'
                            : test.Result < test.general_from
                              ? 'L'
                              : ''
                          : '-'
                      }
                      style={{
                        ...styles.columnText,
                        width: '10%',
                        textAlign: 'center',
                        fontSize: '8px'
                      }}
                    />
                    <Text
                      content={`${test.general_from || '-'} - ${test.general_to || '-'}`}
                      style={{
                        ...styles.columnText,
                        width: '20%',
                        textAlign: 'center',
                        fontSize: '8px'
                      }}
                    />
                    <Text
                      content={test.unit_name || '-'}
                      style={{
                        ...styles.columnText,
                        width: '10%',
                        textAlign: 'center',
                        fontSize: '8px'
                      }}
                    />
                  </View>
                ))}

                {/* Additional Parsed HTML (only on the last page) */}
                {idx === Object.keys(groupedTests).length - 1 && data?.editorContent && (
                  <View style={styles.parsedContent}>
                    {parseHtmlToPdfText(data.editorContent)}
                  </View>
                )}
              </View>
            </Page>
          ))}
      </Document>
    </PDFViewer>

  );
};
